/**
 * Readable Component
 *
 * Wrapper component that marks content as readable for text-to-speech.
 * Provides metadata for reading order and content labeling.
 *
 * Usage:
 * <Readable order={1} label="Page title">
 *   <h1>Welcome to LINA</h1>
 * </Readable>
 *
 * <Readable order={2} label="Introduction">
 *   <p>This is the introduction text...</p>
 * </Readable>
 *
 * WCAG 2.1 Compliance:
 * - 1.3.2 Meaningful Sequence - Content in correct reading order
 * - 4.1.2 Name, Role, Value - Proper semantic markup
 */

import { ReactNode, forwardRef, HTMLAttributes, createElement } from 'react'
import { cn } from '@/lib/utils'

export interface ReadableProps extends HTMLAttributes<HTMLDivElement> {
  /** Reading order (lower numbers read first) */
  order: number

  /** Descriptive label for screen readers and TTS (e.g., "Page title", "Search results") */
  label?: string

  /** Children to render */
  children: ReactNode

  /** Additional CSS classes */
  className?: string

  /** Whether this element should be highlighted when being read */
  highlightOnRead?: boolean

  /** Custom HTML tag to use (default: div) */
  as?: keyof React.JSX.IntrinsicElements
}

/**
 * Readable wrapper component
 *
 * Marks content as readable with metadata attributes that can be
 * discovered by the reading engine.
 */
export const Readable = forwardRef<HTMLDivElement, ReadableProps>(
  (
    { order, label, children, className, highlightOnRead = true, as: Component = 'div', ...props },
    ref,
  ) => {
    return createElement(
      Component,
      {
        ref,
        'data-readable': 'true',
        'data-read-order': order,
        'data-read-label': label,
        'data-highlight-on-read': highlightOnRead,
        className: cn('readable-content', className),
        ...props,
      },
      children,
    )
  },
)

Readable.displayName = 'Readable'

/**
 * Utility function to extract text from a readable element
 */
export function extractReadableText(element: HTMLElement): string {
  // Get all text nodes, excluding hidden elements
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      const parent = node.parentElement
      if (!parent) return NodeFilter.FILTER_REJECT

      // Skip hidden elements
      if (parent.hasAttribute('hidden') || parent.getAttribute('aria-hidden') === 'true') {
        return NodeFilter.FILTER_REJECT
      }

      const style = window.getComputedStyle(parent)
      if (style.display === 'none' || style.visibility === 'hidden') {
        return NodeFilter.FILTER_REJECT
      }

      return NodeFilter.FILTER_ACCEPT
    },
  })

  const texts: string[] = []
  let node: Node | null

  while ((node = walker.nextNode())) {
    const text = node.textContent?.trim()
    if (text && text.length > 0) {
      texts.push(text)
    }
  }

  return texts.join(' ').replace(/\s+/g, ' ').trim()
}

/**
 * Utility function to find all readable elements on the page
 */
export function findReadableElements(): HTMLElement[] {
  const elements = Array.from(document.querySelectorAll<HTMLElement>('[data-readable="true"]'))

  // Sort by reading order
  return elements.sort((a, b) => {
    const orderA = parseInt(a.getAttribute('data-read-order') || '999', 10)
    const orderB = parseInt(b.getAttribute('data-read-order') || '999', 10)
    return orderA - orderB
  })
}

/**
 * Utility function to find the closest readable element to a clicked target
 */
export function findClosestReadable(target: HTMLElement): HTMLElement | null {
  // Check if target itself is readable
  if (target.hasAttribute('data-readable')) {
    return target
  }

  // Find closest parent that is readable
  return target.closest<HTMLElement>('[data-readable="true"]')
}

/**
 * Interface for readable element metadata
 */
export interface ReadableElementMeta {
  element: HTMLElement
  order: number
  label: string | null
  text: string
  highlightOnRead: boolean
}

/**
 * Extract metadata from a readable element
 */
export function getReadableMetadata(element: HTMLElement): ReadableElementMeta {
  return {
    element,
    order: parseInt(element.getAttribute('data-read-order') || '999', 10),
    label: element.getAttribute('data-read-label'),
    text: extractReadableText(element),
    highlightOnRead: element.getAttribute('data-highlight-on-read') !== 'false',
  }
}
